import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { secureAxios } from "@/middleware/encryptionMiddleware";
import {
  getAccessToken,
  clearAccessToken,
  setOrganizationId,
  getOrganizationId,
  getUserId,
} from "@/utils/token_functions";
import { BASE_URL } from "@/config/api";

export const createOrganization = createAsyncThunk(
  "kycSetup/createOrganization",
  async (organizationData, { rejectWithValue }) => {
    try {
      const accessToken = getAccessToken();
      const ownerID =
        organizationData.ownerID ||
        organizationData.userID ||
        getUserId() ||
        undefined;

      const requestBody = {
        name: organizationData.name,
        type: organizationData.type,
        address: organizationData.address,
        email: organizationData.email,
        phone: organizationData.phone,
        ownerProfileImage: organizationData.ownerProfileImage,
        ownerID,
        ownerFullName: organizationData.ownerFullName,
        ownerEmail: organizationData.ownerEmail,
        ownerPhone: organizationData.ownerPhone,
        isOwner: organizationData.isOwner,
        ownerIdentificationType: organizationData.ownerIdentificationType,
        ownerIdentificationImage: organizationData.ownerIdentificationImage,
        modeOfIdentification: organizationData.modeOfIdentification,
        identificationImage: organizationData.identificationImage,
        CACRegistrationCertificateNumber:
          organizationData.CACRegistrationCertificateNumber,
        CACRegistrationCertificateImage:
          organizationData.CACRegistrationCertificateImage,
        role: organizationData.role,
      };

      const response = await secureAxios.post(
        `${BASE_URL}/v1/organization`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.data?._id) {
        setOrganizationId(response.data.data._id);
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        clearAccessToken();
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateOrganization = createAsyncThunk(
  "kycSetup/updateOrganization",
  async (organizationData, { rejectWithValue }) => {
    try {
      const accessToken = getAccessToken();
      const organizationID = getOrganizationId();
      const currentUserId = getUserId();

      const payload = {
        ...organizationData,
        ...(organizationID ? { _id: organizationID } : {}),
        ownerID:
          organizationData.ownerID ||
          organizationData.userID ||
          currentUserId ||
          undefined,
      };

      const response = await secureAxios.patch(
        `${BASE_URL}/v1/organization`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        clearAccessToken();
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Settings Menu + Manage KYC + Corporate Info all hydrate org data on mount.
// Without caching this hammers /organization/by-any and can trip API 429s.
const ORG_FETCH_TTL_MS = 60 * 1000;
let orgFetchInFlight = false;

// Pass `{ force: true }` after mutations so the UI refreshes immediately.
export const fetchOrganizationById = createAsyncThunk(
  "kycSetup/fetchOrganizationById",
  async (_arg, { rejectWithValue }) => {
    try {
      const accessToken = getAccessToken();
      const organizationId = getOrganizationId();

      if (!organizationId) {
        return rejectWithValue("No organization ID found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/organization/by-any/_id/${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        clearAccessToken();
      }
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      orgFetchInFlight = false;
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force === true) {
        orgFetchInFlight = true;
        return true;
      }

      const orgState = getState().organization || {};
      if (orgState.isLoading || orgFetchInFlight) return false;

      const hasData =
        Array.isArray(orgState.organizationData) &&
        orgState.organizationData.length > 0;
      const isFresh =
        orgState.lastFetchTime &&
        Date.now() - orgState.lastFetchTime < ORG_FETCH_TTL_MS;

      // Reuse Redux cache when still within TTL.
      if (hasData && isFresh) return false;

      // Reserve the in-flight slot before the payload runs (covers StrictMode double-mount).
      orgFetchInFlight = true;
      return true;
    },
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  step1: {
    name: "",
    type: "",
    role: "",
    address: "",
    ownerProfileImage: null,
    ownerProfilePreview: null,
    email: "",
    phone: "",
  },
  step2: {
    ownerFullName: "",
    ownerEmail: "",
    ownerPhone: "",
    isOwner: false,
    ownerID: null,
    modeOfIdentification: "",
    identificationImage: null,
  },
  step3: {
    CACRegistrationCertificateNumber: "",
    CACRegistrationCertificateImage: null,
    modeOfIdentification: "",
    identificationImage: null,
  },
  review: {
    termsAccepted: false,
    isSubmitted: false,
  },
  currentStep: 1,
  isLoading: false,
  error: null,
  organizationData: null,
  lastFetchTime: null,
  isComplete: false,
  isOrganizationCreated: false,
};

/** Prefer non-empty values left→right so API omissions don't wipe submitted KYC fields. */
const pickDefined = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return values.find((v) => v !== undefined) ?? null;
};

const normalizeOrgRecord = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value[0] || null;
  if (value.data) return normalizeOrgRecord(value.data);
  return value;
};

/**
 * Merge prior Redux org + request payload + API response.
 * Review/submit sends complete KYC, but PATCH/GET responses often omit file fields.
 */
const mergeOrganizationRecord = (previous, submitted, response) => {
  const prev = normalizeOrgRecord(previous) || {};
  const submit = normalizeOrgRecord(submitted) || {};
  const res = normalizeOrgRecord(response) || {};

  return {
    ...prev,
    ...submit,
    ...res,
    name: pickDefined(res.name, submit.name, prev.name),
    type: pickDefined(res.type, submit.type, prev.type),
    address: pickDefined(res.address, submit.address, prev.address),
    email: pickDefined(res.email, submit.email, prev.email),
    phone: pickDefined(res.phone, submit.phone, prev.phone),
    role: pickDefined(res.role, submit.role, prev.role),
    ownerFullName: pickDefined(
      res.ownerFullName,
      submit.ownerFullName,
      prev.ownerFullName
    ),
    ownerEmail: pickDefined(res.ownerEmail, submit.ownerEmail, prev.ownerEmail),
    ownerPhone: pickDefined(res.ownerPhone, submit.ownerPhone, prev.ownerPhone),
    ownerID: pickDefined(res.ownerID, submit.ownerID, prev.ownerID),
    ownerIdentificationType: pickDefined(
      res.ownerIdentificationType,
      submit.ownerIdentificationType,
      prev.ownerIdentificationType
    ),
    ownerIdentificationImage: pickDefined(
      res.ownerIdentificationImage,
      submit.ownerIdentificationImage,
      prev.ownerIdentificationImage
    ),
    CACRegistrationCertificateNumber: pickDefined(
      res.CACRegistrationCertificateNumber,
      res.cacRegistrationCertificateNumber,
      submit.CACRegistrationCertificateNumber,
      prev.CACRegistrationCertificateNumber
    ),
    CACRegistrationCertificateImage: pickDefined(
      res.CACRegistrationCertificateImage,
      res.cacRegistrationCertificateImage,
      submit.CACRegistrationCertificateImage,
      prev.CACRegistrationCertificateImage
    ),
    modeOfIdentification: pickDefined(
      res.modeOfIdentification,
      submit.modeOfIdentification,
      prev.modeOfIdentification
    ),
    identificationImage: pickDefined(
      res.identificationImage,
      submit.identificationImage,
      prev.identificationImage
    ),
    ownerProfileImage: pickDefined(
      res.ownerProfileImage,
      submit.ownerProfileImage,
      prev.ownerProfileImage
    ),
    status: pickDefined(res.status, submit.status, prev.status),
    _id: pickDefined(res._id, submit._id, prev._id),
  };
};

const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    updateStep: (state, action) => {
      const { step, data } = action.payload;
      state[step] = { ...state[step], ...data };
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    resetOrganization: () => initialState,
    acceptTerms: (state, action) => {
      state.review.termsAccepted = action.payload;
    },
    setSubmissionStatus: (state, action) => {
      state.review.isSubmitted = action.payload;
    },
    setOrganizationCreated: (state, action) => {
      state.isOrganizationCreated = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        const merged = mergeOrganizationRecord(
          state.organizationData,
          action.meta?.arg,
          action.payload?.data ?? action.payload
        );
        state.organizationData = merged?._id || merged?.name ? [merged] : [];
        state.lastFetchTime = Date.now();
        state.isComplete = true;
        state.review.isSubmitted = true;
        state.isOrganizationCreated = true;
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        const merged = mergeOrganizationRecord(
          state.organizationData,
          action.meta?.arg,
          action.payload?.data ?? action.payload
        );
        state.organizationData =
          merged?._id || merged?.name ? [merged] : state.organizationData;
        state.lastFetchTime = Date.now();
        state.isComplete = true;
        state.review.isSubmitted = true;
        state.isOrganizationCreated = true;
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrganizationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lastFetchTime = Date.now();

        const raw = action.payload?.data;
        const orgs = Array.isArray(raw) ? raw : raw ? [raw] : [];
        // Merge so a thin GET doesn't erase CAC / ID docs we already know about.
        const merged = mergeOrganizationRecord(
          state.organizationData,
          null,
          orgs[0]
        );
        state.organizationData = merged?._id || merged?.name ? [merged] : orgs;

        if (orgs.length > 0) {
          state.isOrganizationCreated = true;
          const org = orgs[0];

          state.step1 = {
            ...state.step1,
            name: org.name || state.step1.name,
            type: org.type || state.step1.type,
            role: org.role || state.step1.role,
            address: org.address || state.step1.address,
            email: org.email || state.step1.email,
            phone: org.phone || state.step1.phone,
            ownerProfileImage:
              org.ownerProfileImage || state.step1.ownerProfileImage,
          };
          state.step2 = {
            ...state.step2,
            ownerFullName: org.ownerFullName || state.step2.ownerFullName,
            ownerEmail: org.ownerEmail || state.step2.ownerEmail,
            ownerPhone: org.ownerPhone || state.step2.ownerPhone,
            isOwner:
              typeof org.isOwner === "boolean"
                ? org.isOwner
                : state.step2.isOwner,
            ownerID: org.ownerID || state.step2.ownerID,
            modeOfIdentification:
              org.ownerIdentificationType ||
              org.modeOfIdentification ||
              state.step2.modeOfIdentification,
            identificationImage:
              org.ownerIdentificationImage || state.step2.identificationImage,
          };
          state.step3 = {
            ...state.step3,
            CACRegistrationCertificateNumber:
              org.CACRegistrationCertificateNumber ||
              state.step3.CACRegistrationCertificateNumber,
            CACRegistrationCertificateImage:
              org.CACRegistrationCertificateImage ||
              state.step3.CACRegistrationCertificateImage,
            modeOfIdentification:
              org.modeOfIdentification || state.step3.modeOfIdentification,
            identificationImage:
              org.identificationImage || state.step3.identificationImage,
          };
        }
      })
      .addCase(fetchOrganizationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateStep,
  setCurrentStep,
  resetOrganization,
  acceptTerms,
  setSubmissionStatus,
  setOrganizationCreated,
} = organizationSlice.actions;

export const selectOrganization = (state) => state.organization;
export const selectStep1Data = (state) => state.organization?.step1;
export const selectStep2Data = (state) => state.organization?.step2;
export const selectStep3Data = (state) => state.organization?.step3;
export const selectReviewData = (state) => state.organization?.review;
export const selectCurrentStep = (state) => state.organization?.currentStep;
export const selectIsLoading = (state) => state.organization?.isLoading;
export const selectError = (state) => state.organization?.error;
export const selectIsComplete = (state) => state.organization?.isComplete;
export const selectIsOrganizationCreated = (state) =>
  state.organization.isOrganizationCreated;

export default organizationSlice.reducer;
